// ============================================================
// Project Access Manager Component
// ============================================================
// Reusable component for managing project member access
// Supports adding/removing users with role-based permissions
// ============================================================

import React, { useState } from 'react';
import { useAnalysis } from '../contexts/AnalysisContext';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import {
    Users,
    UserPlus,
    UserMinus,
    Shield,
    Eye,
    Edit3,
    X,
    Check,
    Mail,
    AlertCircle,
} from 'lucide-react';
import clsx from 'clsx';
import type { ProjectMember } from '../services/analysisService';

// ============================================================
// SUB-COMPONENTS (Low Complexity)
// ============================================================

interface RoleBadgeProps {
    role: ProjectMember['role'];
}

const RoleBadge: React.FC<RoleBadgeProps> = ({ role }) => {
    const config = {
        admin: { icon: Shield, color: 'text-red-500', bg: 'bg-red-100 dark:bg-red-900/30' },
        editor: { icon: Edit3, color: 'text-blue-500', bg: 'bg-blue-100 dark:bg-blue-900/30' },
        viewer: { icon: Eye, color: 'text-slate-500', bg: 'bg-slate-100 dark:bg-slate-700' },
    }[role];

    const Icon = config.icon;

    return (
        <span className={clsx('inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium', config.bg)}>
            <Icon size={12} className={config.color} />
            {role.charAt(0).toUpperCase() + role.slice(1)}
        </span>
    );
};

interface MemberRowProps {
    member: ProjectMember;
    canRemove: boolean;
    onRemove: () => void;
    isDark: boolean;
}

const MemberRow: React.FC<MemberRowProps> = ({ member, canRemove, onRemove, isDark }) => (
    <div className={clsx(
        'flex items-center justify-between p-3 rounded-lg',
        isDark ? 'bg-slate-700/50' : 'bg-slate-50'
    )}>
        <div className="flex items-center gap-3">
            <div className={clsx(
                'w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold',
                isDark ? 'bg-primary-600 text-white' : 'bg-primary-100 text-primary-700'
            )}>
                {member.displayName?.charAt(0).toUpperCase() || member.email.charAt(0).toUpperCase()}
            </div>
            <div>
                <div className={clsx('font-medium', isDark ? 'text-white' : 'text-slate-800')}>
                    {member.displayName || member.email.split('@')[0]}
                </div>
                <div className="text-xs text-slate-500">{member.email}</div>
            </div>
        </div>
        <div className="flex items-center gap-2">
            <RoleBadge role={member.role} />
            {canRemove && (
                <button
                    onClick={onRemove}
                    className="p-1.5 text-red-500 hover:bg-red-100 dark:hover:bg-red-900/30 rounded"
                    title="Remove member"
                >
                    <UserMinus size={16} />
                </button>
            )}
        </div>
    </div>
);

// ============================================================
// ADD MEMBER FORM
// ============================================================

interface AddMemberFormProps {
    onAdd: (email: string, role: ProjectMember['role']) => Promise<void>;
    onCancel: () => void;
    isDark: boolean;
}

const AddMemberForm: React.FC<AddMemberFormProps> = ({ onAdd, onCancel, isDark }) => {
    const [email, setEmail] = useState('');
    const [role, setRole] = useState<ProjectMember['role']>('viewer');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!email.includes('@')) {
            setError('Please enter a valid email address');
            return;
        }

        setIsSubmitting(true);
        setError(null);
        try {
            await onAdd(email, role);
            setEmail('');
            setRole('viewer');
        } catch (err: any) {
            setError(err.message || 'Failed to add member');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <form onSubmit={handleSubmit} className="space-y-3">
            {error && (
                <div className="flex items-center gap-2 text-red-500 text-sm p-2 rounded bg-red-100 dark:bg-red-900/30">
                    <AlertCircle size={16} />
                    {error}
                </div>
            )}

            <div className="flex gap-2">
                <div className="flex-1 relative">
                    <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                        type="email"
                        placeholder="Enter email address"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        className={clsx(
                            'w-full pl-10 pr-3 py-2 rounded-lg border text-sm',
                            isDark
                                ? 'bg-slate-700 border-slate-600 text-white placeholder-slate-400'
                                : 'bg-white border-slate-300 text-slate-800'
                        )}
                        required
                    />
                </div>

                <select
                    value={role}
                    onChange={e => setRole(e.target.value as ProjectMember['role'])}
                    className={clsx(
                        'px-3 py-2 rounded-lg border text-sm',
                        isDark ? 'bg-slate-700 border-slate-600 text-white' : 'bg-white border-slate-300'
                    )}
                >
                    <option value="viewer">Viewer</option>
                    <option value="editor">Editor</option>
                    <option value="admin">Admin</option>
                </select>
            </div>

            <div className="flex justify-end gap-2">
                <button
                    type="button"
                    onClick={onCancel}
                    className={clsx(
                        'px-3 py-1.5 rounded text-sm',
                        isDark ? 'hover:bg-slate-700 text-slate-300' : 'hover:bg-slate-100 text-slate-600'
                    )}
                >
                    Cancel
                </button>
                <button
                    type="submit"
                    disabled={isSubmitting}
                    className="flex items-center gap-2 px-3 py-1.5 bg-primary-500 hover:bg-primary-600 text-white rounded text-sm disabled:opacity-50"
                >
                    {isSubmitting ? (
                        <span className="animate-spin">⟳</span>
                    ) : (
                        <Check size={16} />
                    )}
                    Add Member
                </button>
            </div>
        </form>
    );
};

// ============================================================
// MAIN COMPONENT
// ============================================================

interface ProjectAccessManagerProps {
    isOpen: boolean;
    onClose: () => void;
    projectName: string;
}

export const ProjectAccessManager: React.FC<ProjectAccessManagerProps> = ({
    isOpen,
    onClose,
    projectName,
}) => {
    const { theme } = useTheme();
    const { user } = useAuth();
    const { projectMembers, addMember, removeMember } = useAnalysis();
    const [showAddForm, setShowAddForm] = useState(false);
    const isDark = theme === 'dark';

    // Current user's role in this project
    const currentMember = projectMembers.find(m => m.email === user?.email);
    const isAdmin = currentMember?.role === 'admin' || user?.role === 'org_admin' || user?.role === 'platform_admin';

    if (!isOpen) return null;

    const handleAddMember = async (email: string, role: ProjectMember['role']) => {
        await addMember(email, role);
        setShowAddForm(false);
    };

    const handleRemoveMember = (uid: string) => {
        if (window.confirm('Are you sure you want to remove this member?')) {
            removeMember(uid);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
            <div className={clsx(
                'w-full max-w-lg rounded-xl shadow-xl',
                isDark ? 'bg-slate-800 border border-slate-700' : 'bg-white'
            )}>
                {/* Header */}
                <div className={clsx(
                    'flex items-center justify-between px-5 py-4 border-b',
                    isDark ? 'border-slate-700' : 'border-slate-200'
                )}>
                    <div className="flex items-center gap-3">
                        <Users size={24} className="text-primary-500" />
                        <div>
                            <h2 className={clsx('text-lg font-semibold', isDark ? 'text-white' : 'text-slate-800')}>
                                Project Access
                            </h2>
                            <p className="text-xs text-slate-500">{projectName}</p>
                        </div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-slate-100 dark:hover:bg-slate-700 rounded">
                        <X size={20} className="text-slate-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-5 space-y-4 max-h-[60vh] overflow-y-auto">
                    {/* Members List */}
                    {projectMembers.length === 0 ? (
                        <div className="text-center py-8 text-slate-500">
                            No members yet. Add the first member below.
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {projectMembers.map(member => (
                                <MemberRow
                                    key={member.uid || member.email}
                                    member={member}
                                    canRemove={isAdmin && member.email !== user?.email}
                                    onRemove={() => handleRemoveMember(member.uid)}
                                    isDark={isDark}
                                />
                            ))}
                        </div>
                    )}

                    {/* Add Member Section */}
                    {isAdmin && (
                        <div className={clsx(
                            'pt-4 border-t',
                            isDark ? 'border-slate-700' : 'border-slate-200'
                        )}>
                            {showAddForm ? (
                                <AddMemberForm
                                    onAdd={handleAddMember}
                                    onCancel={() => setShowAddForm(false)}
                                    isDark={isDark}
                                />
                            ) : (
                                <button
                                    onClick={() => setShowAddForm(true)}
                                    className={clsx(
                                        'w-full flex items-center justify-center gap-2 py-3 rounded-lg border-2 border-dashed',
                                        isDark
                                            ? 'border-slate-600 hover:border-primary-500 text-slate-400 hover:text-primary-400'
                                            : 'border-slate-300 hover:border-primary-500 text-slate-500 hover:text-primary-600'
                                    )}
                                >
                                    <UserPlus size={18} />
                                    Add Team Member
                                </button>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className={clsx(
                    'px-5 py-3 border-t flex items-center justify-between text-xs',
                    isDark ? 'border-slate-700 bg-slate-800/50' : 'border-slate-200 bg-slate-50'
                )}>
                    <span className="text-slate-500">
                        {projectMembers.length} member{projectMembers.length !== 1 ? 's' : ''}
                    </span>
                    {!isAdmin && (
                        <span className="text-amber-500 flex items-center gap-1">
                            <AlertCircle size={12} />
                            Admin access required to manage members
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProjectAccessManager;
